import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ChevronRight, Users, AlertTriangle, Trash2,
  FileText, Search, X, ChevronUp, ChevronDown, UserX,
  Phone, Linkedin,
} from "lucide-react";
import ResumePanel from "../components/ResumePanel";
import { useAuth } from "../lib/auth";

// ── constants ──────────────────────────────────────────────────────────────────

const SCORE_OPTIONS = [
  { label: "Any score", value: 0 },
  { label: "60%+", value: 60 },
  { label: "70%+", value: 70 },
  { label: "80%+", value: 80 },
  { label: "90%+", value: 90 },
];

const EMPTY_FILTERS = { search: "", minScore: 0, role: "", workAuth: "" };

// ── helpers ────────────────────────────────────────────────────────────────────

function daysBetween(isoDate) {
  if (!isoDate) return null;
  return Math.ceil((new Date(isoDate) - new Date()) / (1000 * 60 * 60 * 24));
}

function ExpiryBadge({ candidate }) {
  const isExpired = candidate.consent_status === "consent_expired";
  if (isExpired)
    return <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-md bg-[#E11D48]/10 text-[#E11D48] border border-[#E11D48]/30">EXPIRED</span>;
  const days = daysBetween(candidate.consent_expiry_date);
  if (days === null) return null;
  if (days <= 14) return <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-md bg-[#E11D48]/10 text-[#E11D48] border border-[#E11D48]/30">{days}d left</span>;
  if (days <= 60) return <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-md bg-[#F59E0B]/10 text-[#B45309] border border-[#F59E0B]/30">{days}d left</span>;
  return <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-md bg-[#10B981]/10 text-[#047857] border border-[#10B981]/30">{days}d left</span>;
}

function SectionBadge({ section }) {
  if (section === "expiring_soon")
    return <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-md bg-[#F59E0B]/10 text-[#B45309] border border-[#F59E0B]/30">EXPIRING</span>;
  if (section === "recently_expired")
    return <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-md bg-[#E11D48]/10 text-[#E11D48] border border-[#E11D48]/30">EXPIRED</span>;
  return <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-md bg-[#10B981]/10 text-[#047857] border border-[#10B981]/30">ACTIVE</span>;
}

function scoreColor(v) {
  if (v >= 75) return "text-[#10B981] font-semibold";
  if (v >= 50) return "text-[#F59E0B] font-semibold";
  return "text-[#E11D48] font-semibold";
}

function SortIcon({ col, sortState }) {
  if (sortState.col !== col) return <ChevronDown className="w-3 h-3 opacity-30" />;
  return sortState.dir === "asc"
    ? <ChevronUp className="w-3 h-3 text-[#0025D8]" />
    : <ChevronDown className="w-3 h-3 text-[#0025D8]" />;
}

// ── AddedByCell ────────────────────────────────────────────────────────────────

function AddedByCell({ value }) {
  if (!value) return <span className="font-mono text-[9px] text-muted-foreground">—</span>;

  const isFormer = value.startsWith("Former employee:");
  if (isFormer) {
    return (
      <div className="flex items-center gap-1" title={value}>
        <UserX className="w-3 h-3 text-[#E11D48]/60 shrink-0" />
        <span className="font-mono text-[9px] text-muted-foreground/60 italic truncate max-w-[110px]">
          {value.replace("Former employee: ", "").replace(" (removed)", "")}
        </span>
      </div>
    );
  }
  if (value === "You") {
    return <span className="font-mono text-[9px] text-muted-foreground">You</span>;
  }
  return <span className="font-mono text-[10px] font-medium truncate max-w-[120px] block" title={value}>{value}</span>;
}

// ── CandidateRow ───────────────────────────────────────────────────────────────

function CandidateRow({ candidate, onViewResume, showSectionBadge, isAdmin }) {
  const isExpired = candidate.consent_status === "consent_expired";

  return (
    <tr className="border-b border-border hover:bg-[#FAFAFA] transition-colors">
      <td className="px-5 py-3">
        <div className="font-medium text-sm">{candidate.name || <span className="text-muted-foreground italic">PII Wiped</span>}</div>
        {candidate.email && (
          <div className="font-mono text-[9px] text-muted-foreground mt-0.5">{candidate.email}</div>
        )}
        {candidate.phone && (
          <div className="flex items-center gap-1 mt-0.5">
            <Phone className="w-2.5 h-2.5 text-muted-foreground/60 shrink-0" />
            <span className="font-mono text-[9px] text-muted-foreground">{candidate.phone}</span>
          </div>
        )}
        {candidate.linkedin_url && (
          <div className="flex items-center gap-1 mt-0.5">
            <Linkedin className="w-2.5 h-2.5 text-[#0A66C2]/70 shrink-0" />
            <a
              href={candidate.linkedin_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="font-mono text-[9px] text-[#0A66C2] hover:underline truncate max-w-[180px] block"
              title={candidate.linkedin_url}
            >
              LinkedIn ↗
            </a>
          </div>
        )}
      </td>
      <td className="px-5 py-3">
        <div className="font-mono text-[10px] text-muted-foreground">{candidate.role_title || "—"}</div>
        {candidate.work_authorization && (
          <div className="font-mono text-[9px] text-muted-foreground/70 mt-0.5 truncate max-w-[140px]" title={candidate.work_authorization}>
            {candidate.work_authorization}
          </div>
        )}
      </td>
      {isAdmin && (
        <td className="px-5 py-3">
          <AddedByCell value={candidate.added_by} />
        </td>
      )}
      <td className="px-5 py-3 text-center">
        {candidate.match_score != null
          ? <span className={`font-mono text-sm ${scoreColor(candidate.match_score)}`}>{Math.round(candidate.match_score)}%</span>
          : <span className="font-mono text-[9px] text-muted-foreground">—</span>}
      </td>
      <td className="px-5 py-3 text-center">
        {candidate.interview_score != null
          ? <span className={`font-mono text-sm ${scoreColor(candidate.interview_score)}`}>{Math.round(candidate.interview_score)}%</span>
          : <span className="font-mono text-[9px] text-muted-foreground">—</span>}
      </td>
      <td className="px-5 py-3 text-center">
        <div className="flex flex-col items-center gap-0.5">
          {showSectionBadge && <SectionBadge section={candidate._section} />}
          {!showSectionBadge && <ExpiryBadge candidate={candidate} />}
          {candidate.consent_expiry_date && !isExpired && (
            <div className="font-mono text-[9px] text-muted-foreground">{candidate.consent_expiry_date}</div>
          )}
          {isExpired && candidate.pii_wiped_at && (
            <div className="font-mono text-[9px] text-muted-foreground">wiped {candidate.pii_wiped_at}</div>
          )}
        </div>
      </td>
      <td className="px-5 py-3 text-right">
        <button
          onClick={() => candidate.has_resume && onViewResume(candidate)}
          disabled={!candidate.has_resume}
          className={`transition-colors ${candidate.has_resume ? "text-muted-foreground hover:text-[#0025D8]" : "text-muted-foreground/25 cursor-not-allowed"}`}
          title={isExpired ? "Resume wiped — consent expired" : candidate.has_resume ? "View resume" : "No resume on file"}
        >
          <FileText className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

// ── Section (unfiltered view) ──────────────────────────────────────────────────

function Section({ title, icon: Icon, count, accentClass, children, emptyText, isAdmin }) {
  return (
    <div className="rounded-xl border border-border bg-white">
      <div className={`px-6 py-4 border-b border-border flex items-center gap-2 ${accentClass}`}>
        <Icon className="w-4 h-4" />
        <span className="font-mono text-[10px] tracking-[0.2em] font-semibold">{title}</span>
        <span className="ml-auto font-mono text-[10px] text-muted-foreground">{count} candidate{count !== 1 ? "s" : ""}</span>
      </div>
      {count === 0 ? (
        <div className="px-6 py-8 text-center font-mono text-[11px] text-muted-foreground">{emptyText}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-[#FAFAFA]">
                <th className="px-5 py-2.5 text-left font-mono text-[9px] tracking-[0.15em] text-muted-foreground">CANDIDATE</th>
                <th className="px-5 py-2.5 text-left font-mono text-[9px] tracking-[0.15em] text-muted-foreground">ROLE / AUTH</th>
                {isAdmin && <th className="px-5 py-2.5 text-left font-mono text-[9px] tracking-[0.15em] text-muted-foreground">ADDED BY</th>}
                <th className="px-5 py-2.5 text-center font-mono text-[9px] tracking-[0.15em] text-muted-foreground">MATCH</th>
                <th className="px-5 py-2.5 text-center font-mono text-[9px] tracking-[0.15em] text-muted-foreground">INTERVIEW</th>
                <th className="px-5 py-2.5 text-center font-mono text-[9px] tracking-[0.15em] text-muted-foreground">EXPIRY</th>
                <th className="px-5 py-2.5 text-right font-mono text-[9px] tracking-[0.15em] text-muted-foreground">ACTION</th>
              </tr>
            </thead>
            <tbody>{children}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── FilterBar ─────────────────────────────────────────────────────────────────

function FilterBar({ filters, setFilters, roleOptions, workAuthOptions, totalCount, filteredCount, isFiltered }) {
  return (
    <div className="rounded-xl border border-border bg-white px-5 py-3 flex flex-wrap items-center gap-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          placeholder="Search name, email or phone…"
          className="pl-8 pr-3 py-1.5 rounded-xl border border-border text-sm w-52 focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all font-mono text-[11px]"
        />
      </div>

      {/* Min score */}
      <select
        value={filters.minScore}
        onChange={(e) => setFilters((f) => ({ ...f, minScore: Number(e.target.value) }))}
        className="rounded-xl border border-border px-2.5 py-1.5 text-[11px] font-mono focus:outline-none focus:border-[#0025D8] bg-white transition-all"
      >
        {SCORE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      {/* Role */}
      <select
        value={filters.role}
        onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
        className="rounded-xl border border-border px-2.5 py-1.5 text-[11px] font-mono focus:outline-none focus:border-[#0025D8] bg-white transition-all max-w-[180px]"
      >
        <option value="">All roles</option>
        {roleOptions.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>

      {/* Work auth */}
      <select
        value={filters.workAuth}
        onChange={(e) => setFilters((f) => ({ ...f, workAuth: e.target.value }))}
        className="rounded-xl border border-border px-2.5 py-1.5 text-[11px] font-mono focus:outline-none focus:border-[#0025D8] bg-white transition-all max-w-[200px]"
      >
        <option value="">Any work auth</option>
        {workAuthOptions.map((w) => (
          <option key={w} value={w}>{w}</option>
        ))}
      </select>

      {/* Result count + clear */}
      <div className="ml-auto flex items-center gap-3">
        {isFiltered && (
          <>
            <span className="font-mono text-[10px] text-muted-foreground">
              <span className="text-foreground font-semibold">{filteredCount}</span> of {totalCount} match
            </span>
            <button
              onClick={() => setFilters(EMPTY_FILTERS)}
              className="flex items-center gap-1 font-mono text-[10px] tracking-[0.1em] text-[#E11D48] hover:text-[#B91C1C] transition-colors"
            >
              <X className="w-3 h-3" /> CLEAR
            </button>
          </>
        )}
        {!isFiltered && (
          <span className="font-mono text-[10px] text-muted-foreground">{totalCount} total</span>
        )}
      </div>
    </div>
  );
}

// ── FilteredTable (flat sortable view) ────────────────────────────────────────

function FilteredTable({ candidates, sortState, onSort, onViewResume, isAdmin }) {
  const thClass = "px-5 py-2.5 font-mono text-[9px] tracking-[0.15em] text-muted-foreground cursor-pointer hover:text-foreground select-none transition-colors";

  if (candidates.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-white px-6 py-12 text-center font-mono text-[11px] text-muted-foreground">
        No candidates match the current filters.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-white overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-[#FAFAFA]">
            <th className="px-5 py-2.5 text-left font-mono text-[9px] tracking-[0.15em] text-muted-foreground">CANDIDATE</th>
            <th className="px-5 py-2.5 text-left font-mono text-[9px] tracking-[0.15em] text-muted-foreground">ROLE / AUTH</th>
            {isAdmin && <th className="px-5 py-2.5 text-left font-mono text-[9px] tracking-[0.15em] text-muted-foreground">ADDED BY</th>}
            <th className={`${thClass} text-center`} onClick={() => onSort("match_score")}>
              <div className="flex items-center justify-center gap-1">MATCH <SortIcon col="match_score" sortState={sortState} /></div>
            </th>
            <th className={`${thClass} text-center`} onClick={() => onSort("interview_score")}>
              <div className="flex items-center justify-center gap-1">INTERVIEW <SortIcon col="interview_score" sortState={sortState} /></div>
            </th>
            <th className={`${thClass} text-center`} onClick={() => onSort("consent_expiry_date")}>
              <div className="flex items-center justify-center gap-1">EXPIRY <SortIcon col="consent_expiry_date" sortState={sortState} /></div>
            </th>
            <th className="px-5 py-2.5 text-right font-mono text-[9px] tracking-[0.15em] text-muted-foreground">ACTION</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((c) => (
            <CandidateRow
              key={c.candidate_id}
              candidate={c}
              onViewResume={onViewResume}
              showSectionBadge
              isAdmin={isAdmin}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function TalentPoolPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "company";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resumeCandidate, setResumeCandidate] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortState, setSortState] = useState({ col: "match_score", dir: "desc" });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/company/talent-pool")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load talent pool"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSort = (col) => {
    setSortState((s) => ({
      col,
      dir: s.col === col ? (s.dir === "desc" ? "asc" : "desc") : "desc",
    }));
  };


  // ── derived data ────────────────────────────────────────────────────────────

  const active = useMemo(() => data?.active || [], [data]);
  const expiringSoon = useMemo(() => data?.expiring_soon || [], [data]);
  const recentlyExpired = useMemo(() => data?.recently_expired || [], [data]);

  // Flat list with section tag for badge + filtering
  const allCandidates = useMemo(() => [
    ...expiringSoon.map((c) => ({ ...c, _section: "expiring_soon" })),
    ...active.map((c) => ({ ...c, _section: "active" })),
    ...recentlyExpired.map((c) => ({ ...c, _section: "recently_expired" })),
  ], [active, expiringSoon, recentlyExpired]);

  // Unique filter options derived from data
  const roleOptions = useMemo(() => [...new Set(allCandidates.map((c) => c.role_title).filter(Boolean))].sort(), [allCandidates]);
  const workAuthOptions = useMemo(() => [...new Set(allCandidates.map((c) => c.work_authorization).filter(Boolean))].sort(), [allCandidates]);

  const isFiltered = filters.search.trim() || filters.minScore > 0 || filters.role || filters.workAuth;

  const filteredAndSorted = useMemo(() => {
    if (!isFiltered) return [];
    let result = allCandidates;
    if (filters.search.trim()) {
      const q = filters.search.toLowerCase();
      result = result.filter((c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q)
      );
    }
    if (filters.minScore > 0) {
      result = result.filter((c) => (c.match_score ?? -1) >= filters.minScore);
    }
    if (filters.role) {
      result = result.filter((c) => c.role_title === filters.role);
    }
    if (filters.workAuth) {
      result = result.filter((c) => c.work_authorization === filters.workAuth);
    }
    // Sort
    result = [...result].sort((a, b) => {
      const { col, dir } = sortState;
      const mult = dir === "desc" ? -1 : 1;
      if (col === "name") return mult * (a.name || "").localeCompare(b.name || "");
      if (col === "consent_expiry_date") {
        return mult * ((a.consent_expiry_date || "9999") < (b.consent_expiry_date || "9999") ? -1 : 1);
      }
      // match_score / interview_score — nulls last
      const av = a[col] ?? (dir === "desc" ? -1 : 9999);
      const bv = b[col] ?? (dir === "desc" ? -1 : 9999);
      return mult * (bv - av);
    });
    return result;
  }, [isFiltered, allCandidates, filters, sortState]);

  const totalActive = active.length + expiringSoon.length;

  if (loading) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <main className="max-w-6xl mx-auto px-6 md:px-12 py-10">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground mb-6">
        <Link to="/company/roles" className="hover:text-foreground">ROLES</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground">TALENT POOL</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight text-[#0F172A]">Talent Pool</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isAdmin
              ? "Candidates with active consent across all roles in your company."
              : "Candidates with active consent from your roles."}
          </p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-center">
            <div className="font-display font-bold text-2xl">{totalActive}</div>
            <div className="font-mono text-[9px] text-muted-foreground tracking-[0.15em]">ACTIVE</div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="text-center">
            <div className="font-display font-bold text-2xl text-[#B45309]">{expiringSoon.length}</div>
            <div className="font-mono text-[9px] text-muted-foreground tracking-[0.15em]">EXPIRING SOON</div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="text-center">
            <div className="font-display font-bold text-2xl text-[#E11D48]">{recentlyExpired.length}</div>
            <div className="font-mono text-[9px] text-muted-foreground tracking-[0.15em]">RECENTLY EXPIRED</div>
          </div>
        </div>
      </div>

      {/* Persistent filter bar */}
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        roleOptions={roleOptions}
        workAuthOptions={workAuthOptions}
        totalCount={allCandidates.length}
        filteredCount={filteredAndSorted.length}
        isFiltered={!!isFiltered}
      />

      <div className="space-y-5 mt-5">
        {/* ── Filtered flat view ── */}
        {isFiltered ? (
          <FilteredTable
            candidates={filteredAndSorted}
            sortState={sortState}
            onSort={handleSort}
            onViewResume={setResumeCandidate}
            isAdmin={isAdmin}
          />
        ) : (
          <>
            {/* ── Unfiltered section view ── */}
            {expiringSoon.length > 0 && (
              <Section title="EXPIRING WITHIN 60 DAYS" icon={AlertTriangle} count={expiringSoon.length} accentClass="text-[#B45309] bg-[#F59E0B]/5" emptyText="" isAdmin={isAdmin}>
                {expiringSoon.map((c) => (
                  <CandidateRow key={c.candidate_id} candidate={{ ...c, _section: "expiring_soon" }} onViewResume={setResumeCandidate} isAdmin={isAdmin} />
                ))}
              </Section>
            )}

            <Section title="ACTIVE CONSENTS" icon={Users} count={active.length} accentClass="text-[#047857]" emptyText="No candidates with active consent beyond 60 days." isAdmin={isAdmin}>
              {active.map((c) => (
                <CandidateRow key={c.candidate_id} candidate={c} onViewResume={setResumeCandidate} isAdmin={isAdmin} />
              ))}
            </Section>

            {recentlyExpired.length > 0 && (
              <Section title="RECENTLY EXPIRED — GRACE PERIOD (PII WIPED)" icon={Trash2} count={recentlyExpired.length} accentClass="text-[#E11D48] bg-[#E11D48]/5" emptyText="" isAdmin={isAdmin}>
                {recentlyExpired.map((c) => (
                  <CandidateRow key={c.candidate_id} candidate={{ ...c, _section: "recently_expired" }} onViewResume={setResumeCandidate} isAdmin={isAdmin} />
                ))}
              </Section>
            )}

            {totalActive === 0 && recentlyExpired.length === 0 && (
              <div className="rounded-xl border border-border bg-white p-12 text-center">
                <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-2">EMPTY TALENT POOL</div>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  Candidates who apply through your apply links with consent enabled will appear here automatically.
                </p>
                <Link to="/company/roles" className="inline-block mt-4 font-mono text-[10px] tracking-[0.15em] text-[#0025D8] hover:underline">
                  VIEW ROLES
                </Link>
              </div>
            )}
          </>
        )}
      </div>

      {resumeCandidate && (
        <ResumePanel
          candidate={resumeCandidate}
          roleTitle={resumeCandidate.role_title}
          onClose={() => setResumeCandidate(null)}
        />
      )}

      {/* Info box */}
      <div className="mt-8 rounded-xl border border-border bg-white p-5">
        <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-3">HOW TALENT POOL WORKS</div>
        <div className="space-y-1.5 text-sm text-muted-foreground">
          <p>1. Candidates who give consent via an apply link are automatically added with an expiry date.</p>
          <p>2. The retention period is set in your <Link to="/company/consent" className="text-[#0025D8] hover:underline">Consent Settings</Link> (default 12 months).</p>
          <p>3. Use the filter bar above to find candidates by name, match score, role, or work authorization.</p>
          <p>4. To re-engage a candidate for a new role, share the relevant apply link with them directly.</p>
          <p>5. On expiry: PII is wiped immediately. Records are permanently deleted after a 30-day grace period.</p>
        </div>
      </div>
    </main>
  );
}
